import { HeaderPageTitle } from 'src/context/header-title.context';
import React, { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { useParams, Link } from '@tanstack/react-router';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ArrowSquareOutIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import {
  getIntegrationConfig,
  INTEGRATION_CATEGORIES,
} from 'src/config/integrations';
import {
  useListIntegrations,
  useUpdateIntegration,
} from 'src/api/rpc/hooks/integrations/useIntegrations';
import { IntegrationAvailability, IntegrationKey } from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';
import paths from 'src/paths';

// Import specific settings components
import { FamousIntegrationSettings } from './settings/FamousIntegrationSettings';
import { PlaidIntegrationSettings } from './settings/PlaidIntegrationSettings';
import { PaymentsIntegrationSettings } from './settings/PaymentsIntegrationSettings';
import { OracleFusionIntegrationSettings } from './settings/OracleFusionIntegrationSettings';
import { JiraIntegrationSettings } from './settings/JiraIntegrationSettings';
import { MicrosoftTeamsIntegrationSettings } from './settings/MicrosoftTeamsIntegrationSettings';

// Placeholder for internal integrations that don't need settings UI
const NoSettingsComponent = () => null;

// Mapping of integration keys to their settings components
const INTEGRATION_SETTINGS_COMPONENTS: Record<
  IntegrationKey,
  React.ComponentType<{ availability?: IntegrationAvailability }>
> = {
  [IntegrationKey.Famous]: FamousIntegrationSettings,
  [IntegrationKey.Plaid]: PlaidIntegrationSettings,
  [IntegrationKey.Payments]: PaymentsIntegrationSettings,
  [IntegrationKey.OracleFusion]: OracleFusionIntegrationSettings,
  // Oracle EBS is configured from Organization Settings → Integrations →
  // Oracle EBS; the marketplace detail page has no dedicated settings form.
  [IntegrationKey.OracleEbs]: NoSettingsComponent,
  [IntegrationKey.Jira]: JiraIntegrationSettings,
  [IntegrationKey.MicrosoftTeams]: MicrosoftTeamsIntegrationSettings,
  [IntegrationKey.VoiceAgent]: NoSettingsComponent,
  [IntegrationKey.Slack]: NoSettingsComponent,
};

export function IntegrationDetailPage() {
  const gt = useGT();
  const params = useParams({
    from: '/authenticated/integrations/$integrationKey',
  });
  const integrationKey = params.integrationKey as IntegrationKey;
  const { data: integrationsData, isLoading, refetch } = useListIntegrations();
  const installedIntegrations = integrationsData?.installed ?? {};
  const availability = integrationsData?.availability ?? {};
  const integrationAvailability = availability[integrationKey] ?? undefined;
  const updateIntegration = useUpdateIntegration();
  const { showToast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);

  const integrationConfig = getIntegrationConfig(integrationKey);
  const isInstalled = Boolean(
    installedIntegrations && installedIntegrations[integrationKey],
  );
  const isAvailable = integrationAvailability?.available ?? true;
  const unavailableReason = integrationAvailability?.reason;
  const categoryInfo = INTEGRATION_CATEGORIES[integrationConfig.category];
  const IconComponent = integrationConfig.icon;
  const SettingsComponent = INTEGRATION_SETTINGS_COMPONENTS[integrationKey];

  const handleConnect = async () => {
    if (!integrationConfig.defaultSettings) {
      showToast({
        title: 'Configuration Required',
        message: 'This integration requires manual configuration.',
        color: 'warning',
        icon: 'warning',
      });
      return;
    }

    if (!isAvailable) {
      showToast({
        title: 'Integration Unavailable',
        message:
          unavailableReason ||
          `${integrationConfig.name} cannot be configured right now.`,
        color: 'warning',
        icon: 'warning',
      });
      return;
    }

    setIsConnecting(true);
    try {
      await updateIntegration.mutateAsync({
        key: integrationKey,
        settings: integrationConfig.defaultSettings,
      } as Parameters<typeof updateIntegration.mutateAsync>[0]);

      // Refetch the integrations list to update the UI
      await refetch();

      showToast({
        title: 'Integration Connected',
        message: `${integrationConfig.name} has been successfully connected with default settings.`,
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: 'Connection Failed',
        message: `Failed to connect ${integrationConfig.name}. Please try again.`,
        color: 'error',
        icon: 'error',
      });
    } finally {
      setIsConnecting(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Toolbar: back link left, status badges right */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <Link to={paths.authenticated.integrations.list}>
          <Button variant="ghost" size="sm">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            <T context="navigation link returning to integrations list from integration detail">
              Back to Integrations
            </T>
          </Button>
        </Link>

        <div className="flex flex-wrap items-center gap-2">
          <HeaderPageTitle>{integrationConfig.name}</HeaderPageTitle>
          <div className="rounded-md bg-gray-100 p-1.5">
            <IconComponent className="h-5 w-5 text-gray-700" />
          </div>
          {isInstalled && (
            <Badge
              variant="outline"
              className="bg-green-100 text-green-700 border-green-300"
            >
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              <T context="badge indicating integration is installed on the account">
                Installed
              </T>
            </Badge>
          )}
          <Badge variant="secondary">{categoryInfo.name}</Badge>
          {integrationConfig.isPopular && (
            <Badge variant="outline">
              <T context="badge marking integration as commonly used">
                Popular
              </T>
            </Badge>
          )}
          {!isAvailable && (
            <Badge
              variant="outline"
              className="border-red-300 text-red-700 bg-red-50"
            >
              <T context="badge when integration cannot be configured or used">
                Unavailable
              </T>
            </Badge>
          )}
        </div>
      </div>

      <p className="mb-2 text-sm text-muted-foreground">
        {integrationConfig.longDescription}
      </p>
      {!isAvailable && (
        <p className="mb-2 text-sm text-red-600">
          {unavailableReason ||
            `${integrationConfig.name} cannot be configured right now.`}
        </p>
      )}

      {/* Integration Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
        {/* Left Column - Features & Info */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                <T context="integration detail card heading for feature list">
                  Features
                </T>
              </CardTitle>
              <CardDescription>
                <T context="integration detail card subtitle describing the feature list">
                  What this integration provides
                </T>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {integrationConfig.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircleIcon className="h-4 w-4 text-green-600 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Additional info card could go here */}
          <Card className="mt-2">
            <CardHeader>
              <CardTitle className="text-lg">
                <T context="integration detail card heading for support options">
                  Support
                </T>
              </CardTitle>
              <CardDescription>
                <T context="integration support card encouraging user to get help">
                  Need help with this integration?
                </T>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* <Button variant="outline" size="sm" className="w-full">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Documentation
                </Button> */}
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <a
                    href="mailto:support@gently-ai.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ArrowSquareOutIcon className="h-4 w-4 mr-2" />
                    <T context="link label opening email to support for integration help">
                      Contact Support
                    </T>
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Settings */}
        <div className="lg:col-span-2">
          {isInstalled ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  <T context="integration settings card title when integration is installed">
                    Settings
                  </T>
                </CardTitle>
                <CardDescription>
                  <T context="integration settings card subtitle with dynamic integration product name">
                    Configure your <Var>{integrationConfig.name}</Var>{' '}
                    integration
                  </T>
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!isAvailable && (
                  <p className="text-sm text-red-600 mb-4">
                    {unavailableReason ||
                      `${integrationConfig.name} is currently unavailable.`}
                  </p>
                )}
                {SettingsComponent && (
                  <SettingsComponent availability={integrationAvailability} />
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  <T context="integration connect card title with dynamic integration name">
                    Connect <Var>{integrationConfig.name}</Var>
                  </T>
                </CardTitle>
                <CardDescription>
                  <T context="integration connect card subtitle with dynamic integration name">
                    Start using <Var>{integrationConfig.name}</Var> with your
                    account
                  </T>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <IconComponent className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">
                    <T context="integration connect empty state heading">
                      Ready to connect?
                    </T>
                  </h3>
                  <p className="text-gray-600 mb-4">
                    <T context="integration connect empty state body with dynamic integration name">
                      Connect <Var>{integrationConfig.name}</Var> to start
                      syncing your data and streamlining your workflow.
                    </T>
                  </p>
                  {!isAvailable && (
                    <p className="text-sm text-red-600 mb-4">
                      {unavailableReason ||
                        `${integrationConfig.name} is currently unavailable.`}
                    </p>
                  )}
                  <Button
                    size="lg"
                    className="px-8"
                    onClick={handleConnect}
                    disabled={isConnecting || !isAvailable}
                  >
                    {isConnecting ? (
                      <>
                        <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                        <T context="primary button label while integration connection is in progress">
                          Connecting...
                        </T>
                      </>
                    ) : isAvailable ? (
                      gt('Connect {name}', {
                        context:
                          'primary button label to connect named integration',
                        name: integrationConfig.name,
                      })
                    ) : (
                      gt('Unavailable', {
                        context:
                          'primary button label when integration cannot be connected',
                      })
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
